import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import { 
  HeartHandshake, 
  Clock, 
  Plus, 
  Trash2, 
  Save, 
  ExternalLink,
  Heart,
  GraduationCap,
  Users,
  Star,
  Award,
  Upload,
  Image as ImageIcon,
  Eye,
  X
} from 'lucide-react';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const ICON_OPTIONS = [
  { value: 'fa-heart', label: 'Heart / Peduli (fa-heart)', icon: Heart },
  { value: 'fa-graduation-cap', label: 'Cap / Edukasi (fa-graduation-cap)', icon: GraduationCap },
  { value: 'fa-handshake', label: 'Handshake / Kolaborasi (fa-handshake)', icon: HeartHandshake },
  { value: 'fa-users', label: 'Users / Tim (fa-users)', icon: Users },
  { value: 'fa-star', label: 'Star / Unggul (fa-star)', icon: Star },
  { value: 'fa-award', label: 'Award / Prestasi (fa-award)', icon: Award }
];

const DEFAULT_FORM_DATA = {
  kicker: 'Join Our Team',
  title: 'Philosophy of Hiring and Development',
  description: 'We invest in educators and staff who share our mission, grow with our community, and are committed to nurturing the next generation of learners.',
  items: [
    {
      id: 'purpose-driven-hiring',
      title: 'Purpose-Driven Hiring',
      description: 'We look for educators and staff who share our values, care for students, and commit to growth.',
      key_focus: 'Values-aligned hiring',
      icon: 'fa-heart',
      image: '/assets/news/news3.jpg'
    },
    {
      id: 'professional-development',
      title: 'Professional Development',
      description: 'Ongoing training, coaching, and collaborative planning that help every team member grow.',
      key_focus: 'Continuous growth',
      icon: 'fa-graduation-cap',
      image: '/assets/news/news4.jpg'
    },
    {
      id: 'supportive-culture',
      title: 'Supportive Culture',
      description: 'A workplace rooted in respect, teamwork, and a shared commitment to student wellbeing.',
      key_focus: 'Team collaboration',
      icon: 'fa-handshake',
      image: '/assets/news/news7.jpg'
    }
  ],
  cta: {
    title: 'Ready to Make a Difference?',
    description: 'Join our dedicated team of educators and professionals committed to excellence in education and student development.',
    button_label: 'Explore Open Positions',
    button_url: '#open-positions'
  }
};

export default function CareersPhilosophyForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false,
    items: false,
    cta: false
  });
  const [pendingItems, setPendingItems] = useState({});
  const [pendingDeletes, setPendingDeletes] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);

  const getAuthHeaders = useCallback(() => {
    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (e) {}
    }
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }, []);

  const fetchMediaMap = useCallback(async () => {
    try {
      const cached = sessionStorage.getItem('cms_media_map');
      if (cached) {
        setMediaMap(JSON.parse(cached));
        return;
      }
      const res = await fetch(`${apiBase}/media?limit=500`, { 
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const json = await res.json();
        const items = json.data?.items || json.data || [];
        if (Array.isArray(items)) {
          const map = {};
          items.forEach(m => {
            if (m.id && m.file_path) {
              map[m.id] = m.file_path;
              map[String(m.id)] = m.file_path;
            }
          });
          setMediaMap(map);
          try {
            sessionStorage.setItem('cms_media_map', JSON.stringify(map));
          } catch (e) {}
        }
      }
    } catch (e) {
      console.warn('[CareersPhilosophyForm] Media map fetch error:', e);
    }
  }, [apiBase, getAuthHeaders]);

  const resolveImage = useCallback((imgSrc) => {
    if (!imgSrc) return null;
    const str = String(imgSrc).trim();
    if (!str) return null;
    if (mediaMap[str]) return mediaMap[str];
    const num = Number(str);
    if (!isNaN(num) && mediaMap[num]) return mediaMap[num];
    const resolved = resolveMediaSrc(str, mediaMap);
    if (resolved) {
      if (resolved.startsWith('http://') || resolved.startsWith('https://') || resolved.startsWith('blob:') || resolved.startsWith('data:')) {
        return resolved;
      }
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${resolved.startsWith('/') ? '' : '/'}${resolved}`;
    }
    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('blob:') || str.startsWith('data:')) {
      return str;
    }
    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${str}`;
    }
    return str;
  }, [mediaMap, apiBase]);

  const loadSection = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=careers`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => s.section_type === 'careers-philosophy' || s.section_type === 'philosophy' || s.section_type === 'join-our-team');
      }

      if (!foundSection) {
        const fbRes = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=careers`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (fbRes.ok) {
          const fbJson = await fbRes.json();
          const items = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
          foundSection = items.find(s => s.section_type === 'careers-philosophy' || s.section_type === 'philosophy' || s.section_type === 'join-our-team');
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);
        let content = foundSection.content_published;
        if (!content || content === '{}' || content === '[]') {
          content = foundSection.content;
        }
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content);
          } catch (e) {
            content = {};
          }
        }

        const en = content?.en || content || {};
        const rawItems = Array.isArray(en.items) && en.items.length > 0 
          ? en.items 
          : (Array.isArray(en.hiring_philosophy) && en.hiring_philosophy.length > 0 ? en.hiring_philosophy : DEFAULT_FORM_DATA.items);

        const loaded = {
          kicker: en.kicker || 'Join Our Team',
          title: en.title || 'Philosophy of Hiring and Development',
          description: en.description || DEFAULT_FORM_DATA.description,
          items: rawItems.map((item, idx) => ({
            id: item.id || `career-philosophy-${idx + 1}`,
            title: item.title || '',
            description: item.description || '',
            key_focus: item.key_focus || item.keyFocus || '',
            icon: item.icon || (idx === 0 ? 'fa-heart' : idx === 1 ? 'fa-graduation-cap' : 'fa-handshake'),
            image: item.image || ''
          })),
          cta: {
            title: en.cta?.title || DEFAULT_FORM_DATA.cta.title,
            description: en.cta?.description || DEFAULT_FORM_DATA.cta.description,
            button_label: en.cta?.button_label || en.cta?.buttonText || DEFAULT_FORM_DATA.cta.button_label,
            button_url: en.cta?.button_url || en.cta?.buttonLink || DEFAULT_FORM_DATA.cta.button_url
          }
        };

        setFormData(loaded);
        setOriginalData(loaded);

        try {
          const pendingRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendingRes.ok) {
            const pData = await pendingRes.json();
            if (pData.success && pData.data?.pending_changes) {
              const pFields = { kicker: false, title: false, description: false, items: false, cta: false };
              const pItems = {};
              const pDeletes = {};

              pData.data.pending_changes.forEach(c => {
                if (c.status === 'pending') {
                  if (['kicker', 'title', 'description', 'items', 'cta'].includes(c.field_id)) {
                    pFields[c.field_id] = true;
                  } else if (c.field_id?.includes('items.')) {
                    const m = c.field_id.match(/items\.(\d+)\.(add|update|delete)/);
                    if (m) {
                      const idx = m[1];
                      const op = m[2];
                      if (op === 'delete') {
                        pDeletes[`item_${idx}`] = true;
                      } else {
                        pItems[`item_${idx}`] = true;
                      }
                    } else {
                      const mOld = c.field_id.match(/items\.(\d+)/);
                      if (mOld) pItems[`item_${mOld[1]}`] = true;
                    }
                  }
                }
              });

              setPendingFields(pFields);
              setPendingItems(pItems);
              setPendingDeletes(pDeletes);
            }
          }
        } catch (pe) {
          console.warn('[CareersPhilosophyForm] Pending check error:', pe);
        }
      }
    } catch (err) {
      console.error('[CareersPhilosophyForm] Load error:', err);
      toast.error('Gagal memuat data Hiring Philosophy');
    } finally {
      setLoading(false);
    }
  }, [apiBase, getAuthHeaders]);

  useEffect(() => {
    fetchMediaMap();
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const next = [...prev.items];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, items: next };
    });
  };

  const handleCtaChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      cta: { ...prev.cta, [field]: value }
    }));
  };

  const handleImageUpload = (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Ukuran foto maksimal 5MB');
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Format foto harus JPG, PNG, atau WebP');
      return;
    }

    setImageUploading(prev => ({ ...prev, [index]: true }));
    setUploadProgress(prev => ({ ...prev, [index]: 10 }));

    const uploadData = new FormData();
    uploadData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiBase}/media/upload`);
    xhr.withCredentials = true;

    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (err) {}
    }
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        const pct = Math.round((ev.loaded / ev.total) * 100);
        setUploadProgress(prev => ({ ...prev, [index]: pct }));
      }
    };

    xhr.onload = () => {
      setImageUploading(prev => ({ ...prev, [index]: false }));
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const res = JSON.parse(xhr.responseText);
          const data = res.data || res;
          let mediaUrl = data.url || data.file_path || data.file_url || res.url;
          if (mediaUrl && !mediaUrl.startsWith('http://') && !mediaUrl.startsWith('https://')) {
            const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
            mediaUrl = `${origin}${mediaUrl.startsWith('/') ? '' : '/'}${mediaUrl}`;
          }
          if (mediaUrl) {
            handleItemChange(index, 'image', mediaUrl);
            toast.success(`Foto Pilar ${index + 1} berhasil diunggah. Klik "Save Pilar" untuk mengajukan.`);
          } else {
            toast.error('Gagal membaca URL foto');
          }
        } catch (err) {
          toast.error('Gagal memproses respon upload');
        }
      } else {
        toast.error(`Upload gagal (${xhr.status})`);
      }
    };

    xhr.onerror = () => {
      setImageUploading(prev => ({ ...prev, [index]: false }));
      toast.error('Terjadi gangguan jaringan saat upload');
    };

    xhr.send(uploadData);
  };

  const handleAddItem = () => {
    const newItem = {
      id: `philosophy-${Date.now()}`,
      title: 'Pilar Nilai Baru',
      description: 'Penjelasan prinsip pembinaan dan pengembangan tim kerja...',
      key_focus: 'Fokus Utama',
      icon: 'fa-star',
      image: '',
      isNew: true
    };
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    toast.success('Pilar baru ditambahkan ke draf. Isi data dan klik "Save Pilar".');
  };

  const handleRemoveItem = async (index) => {
    const itemToRemove = formData.items[index];

    // If item is new/draft not saved on server yet, remove locally
    if (itemToRemove.isNew && (!originalData.items || !originalData.items[index])) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, idx) => idx !== index)
      }));
      toast.info(`Pilar baru dibatalkan`);
      return;
    }

    if (formData.items.length <= 1) {
      toast.warning('Minimal harus ada 1 pilar filosofi');
      return;
    }

    if (!window.confirm(`Ajukan penghapusan pilar "${itemToRemove.title || 'Untitled'}" ke Kepala Sekolah?`)) {
      return;
    }

    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const key = `del_${index}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `items.${index}.delete`,
          field_name: `Hapus Pilar: ${itemToRemove.title}`,
          old_value: originalData.items?.[index] || itemToRemove,
          new_value: { action: 'delete', id: itemToRemove.id, title: itemToRemove.title },
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Pengajuan hapus pilar "${itemToRemove.title}" berhasil dikirim untuk approval`);
        setPendingDeletes(prev => ({ ...prev, [`item_${index}`]: true }));
        try {
          sessionStorage.removeItem('cms_page_careers');
          sessionStorage.removeItem('cms_sections_careers');
          window.dispatchEvent(new Event('cms-careers-updated'));
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[CareersPhilosophyForm] Submit delete error:', err);
      toast.error('Gagal mengajukan hapus pilar: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleSubmitField = async (field, customValue, fieldDisplayName) => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const valToSubmit = customValue !== undefined ? customValue : formData[field];
    const oldVal = originalData[field];

    setSubmitting(prev => ({ ...prev, [field]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: field,
          field_name: fieldDisplayName || (field.charAt(0).toUpperCase() + field.slice(1)),
          old_value: oldVal,
          new_value: valToSubmit,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`"${fieldDisplayName || field}" berhasil diajukan untuk approval`);
        setPendingFields(prev => ({ ...prev, [field]: true }));
        setOriginalData(prev => ({ ...prev, [field]: valToSubmit }));
        try {
          sessionStorage.removeItem('cms_page_careers');
          sessionStorage.removeItem('cms_sections_careers');
          window.dispatchEvent(new Event('cms-careers-updated'));
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[CareersPhilosophyForm] Submit error:', err);
      toast.error('Error submitting change: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleSubmitItem = async (index) => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const item = formData.items[index];
    const key = `item_${index}`;
    const isNew = item.isNew || !originalData.items || !originalData.items[index];
    const fieldId = isNew ? `items.${index}.add` : `items.${index}.update`;

    setSubmitting(prev => ({ ...prev, [key]: true }));
    try {
      const cleanItem = { ...item };
      delete cleanItem.isNew;

      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldId,
          field_name: `Philosophy Card: ${item.title || `Item ${index + 1}`}`,
          old_value: isNew ? null : (originalData.items?.[index] || {}),
          new_value: cleanItem,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Pilar "${item.title}" berhasil diajukan untuk approval`);
        setPendingItems(prev => ({ ...prev, [key]: true }));
        setOriginalData(prev => {
          const next = [...(prev.items || [])];
          next[index] = cleanItem;
          return { ...prev, items: next };
        });
        // Remove isNew flag locally
        setFormData(prev => {
          const next = [...prev.items];
          next[index] = cleanItem;
          return { ...prev, items: next };
        });
        try {
          sessionStorage.removeItem('cms_page_careers');
          sessionStorage.removeItem('cms_sections_careers');
          window.dispatchEvent(new Event('cms-careers-updated'));
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[CareersPhilosophyForm] Submit error:', err);
      toast.error('Gagal mengajukan pilar: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Hiring Philosophy Section...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Form Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <HeartHandshake size={22} />
        </div>
        <div>
          <h2 className="unified-title">Hiring Philosophy & Culture</h2>
          <p className="unified-subtitle">
            Kelola prinsip perekrutan, pilar nilai pengembangan staf, foto representatif, serta Call to Action (CTA) halaman Careers.
          </p>
        </div>
      </div>

      {/* Section Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={pendingFields.kicker}
            placeholder="e.g. Join Our Team"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker', formData.kicker, 'Section Kicker')}
            disabled={pendingFields.kicker || submitting.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : (pendingFields.kicker ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Section Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Title</label>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={pendingFields.title}
            placeholder="e.g. Philosophy of Hiring and Development"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title', formData.title, 'Section Title')}
            disabled={pendingFields.title || submitting.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : (pendingFields.title ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Section Description */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Description</label>
          {pendingFields.description && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <textarea
            className="unified-textarea"
            rows={2}
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            disabled={pendingFields.description}
            placeholder="We invest in educators and staff who share our mission..."
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('description', formData.description, 'Section Description')}
            disabled={pendingFields.description || submitting.description || formData.description === originalData.description}
          >
            <Save size={14} /> {submitting.description ? 'Saving...' : (pendingFields.description ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Philosophy Cards Grid */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <div>
            <label className="unified-label">Pilar Filosofi Perekrutan ({formData.items.length} Kartu)</label>
            <p className="unified-field-desc" style={{ marginTop: '4px' }}>
              Setiap pilar menampilkan foto opsional, ikon representatif, judul pilar, ringkasan, dan badge sorotan Key Focus.
            </p>
          </div>
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={handleAddItem}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Plus size={16} /> Tambah Pilar
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px', marginTop: '16px' }}>
          {formData.items.map((item, idx) => {
            const isPending = pendingItems[`item_${idx}`];
            const isDeletePending = pendingDeletes[`item_${idx}`];
            const isSubmitting = submitting[`item_${idx}`] || submitting[`del_${idx}`];
            const isUploading = imageUploading[idx];
            const progress = uploadProgress[idx] || 0;
            const resolvedImg = resolveImage(item.image);

            return (
              <div 
                key={idx}
                style={{
                  border: isDeletePending 
                    ? '1.5px dashed #ef4444' 
                    : (isPending ? '1.5px dashed #f59e0b' : '1px solid #e2e8f0'),
                  borderRadius: '14px',
                  background: isDeletePending ? '#fef2f2' : (isPending ? '#fffdf7' : '#ffffff'),
                  boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden'
                }}
              >
                {/* Photo Preview & Upload Bar */}
                <div style={{ position: 'relative', height: '140px', background: '#0f172a' }}>
                  {resolvedImg ? (
                    <img 
                      src={resolvedImg} 
                      alt={item.title || `Pilar ${idx + 1}`} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                      onClick={() => setLightboxImage(resolvedImg)}
                      title="Klik untuk memperbesar foto"
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                      <ImageIcon size={28} />
                      <span style={{ fontSize: '11px', marginTop: '4px' }}>Belum ada foto kartu</span>
                    </div>
                  )}

                  {resolvedImg && (
                    <button
                      type="button"
                      onClick={() => setLightboxImage(resolvedImg)}
                      style={{
                        position: 'absolute',
                        bottom: '8px',
                        left: '8px',
                        background: 'rgba(15, 23, 42, 0.75)',
                        color: '#ffffff',
                        border: 'none',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '10px'
                      }}
                      title="Lihat Pratinjau Foto"
                    >
                      <Eye size={11} /> Pratinjau
                    </button>
                  )}

                  <label 
                    style={{
                      position: 'absolute',
                      bottom: '8px',
                      right: '8px',
                      background: 'rgba(15, 23, 42, 0.85)',
                      color: '#ffffff',
                      padding: '5px 10px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: isDeletePending ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      backdropFilter: 'blur(4px)'
                    }}
                  >
                    <Upload size={12} />
                    <span>{isUploading ? `${progress}%` : (resolvedImg ? 'Ganti Foto' : 'Pilih Foto')}</span>
                    <input 
                      type="file" 
                      accept="image/jpeg,image/png,image/webp" 
                      style={{ display: 'none' }}
                      disabled={isDeletePending || isPending || isUploading}
                      onChange={(e) => handleImageUpload(idx, e)}
                    />
                  </label>

                  <div style={{ position: 'absolute', top: '8px', left: '8px', background: 'rgba(15, 23, 42, 0.8)', color: '#ffffff', padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 800 }}>
                    PILAR #{idx + 1}
                  </div>

                  {isDeletePending && (
                    <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
                      <span className="unified-pending-pill" style={{ background: '#fee2e2', color: '#b91c1c' }}>
                        <Clock size={11} /> Pending Delete
                      </span>
                    </div>
                  )}

                  {!isDeletePending && isPending && (
                    <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
                      <span className="unified-pending-pill" style={{ background: '#fef3c7', color: '#92400e' }}>
                        <Clock size={11} /> Pending Approval
                      </span>
                    </div>
                  )}
                </div>

                {/* Card Top Row: Badge & Delete */}
                <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ 
                      background: '#ede9fe', 
                      color: '#7c3aed', 
                      fontSize: '11px', 
                      fontWeight: 800, 
                      padding: '3px 10px', 
                      borderRadius: '9999px',
                      textTransform: 'uppercase'
                    }}>
                      {item.title || `Pilar ${idx + 1}`}
                    </span>

                    <button
                      type="button"
                      onClick={() => handleRemoveItem(idx)}
                      disabled={isDeletePending || isSubmitting}
                      style={{ 
                        background: isDeletePending ? '#fee2e2' : 'none', 
                        border: 'none', 
                        color: '#ef4444', 
                        cursor: isDeletePending ? 'not-allowed' : 'pointer', 
                        padding: '4px',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '11px'
                      }}
                      title="Hapus Pilar"
                    >
                      <Trash2 size={15} />
                      {isDeletePending && <span>Pending Delete</span>}
                    </button>
                  </div>

                  {/* Select Icon */}
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>
                      Pilih Ikon Pilar
                    </label>
                    <select
                      className="unified-input"
                      value={item.icon || 'fa-heart'}
                      disabled={isDeletePending || isPending}
                      onChange={(e) => handleItemChange(idx, 'icon', e.target.value)}
                      style={{ fontSize: '12.5px', padding: '8px 12px' }}
                    >
                      {ICON_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Title */}
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>
                      Judul Pilar *
                    </label>
                    <input 
                      type="text"
                      className="unified-input"
                      value={item.title || ''}
                      disabled={isDeletePending || isPending}
                      onChange={(e) => handleItemChange(idx, 'title', e.target.value)}
                      placeholder="e.g. Purpose-Driven Hiring"
                      style={{ fontSize: '13px', fontWeight: 600 }}
                    />
                  </div>

                  {/* Key Focus */}
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>
                      Key Focus (Badge Sorotan) *
                    </label>
                    <input 
                      type="text"
                      className="unified-input"
                      value={item.key_focus || ''}
                      disabled={isDeletePending || isPending}
                      onChange={(e) => handleItemChange(idx, 'key_focus', e.target.value)}
                      placeholder="e.g. Values-aligned hiring"
                      style={{ fontSize: '12.5px' }}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>
                      Deskripsi Pilar
                    </label>
                    <textarea 
                      className="unified-textarea"
                      rows={3}
                      value={item.description || ''}
                      disabled={isDeletePending || isPending}
                      onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                      placeholder="We look for educators and staff who share our values..."
                      style={{ fontSize: '12.5px', minHeight: '70px' }}
                    />
                  </div>

                  {/* Card Submit Action */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'auto', paddingTop: '10px', borderTop: '1px solid #f1f5f9' }}>
                    <button
                      type="button"
                      className="unified-submit-button"
                      onClick={() => handleSubmitItem(idx)}
                      disabled={isDeletePending || isPending || isSubmitting}
                      style={{ padding: '6px 14px', fontSize: '12px' }}
                    >
                      <Save size={13} />
                      {isSubmitting ? 'Saving...' : (isDeletePending ? 'Pending Delete' : (isPending ? 'Pending' : 'Save Pilar'))}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Philosophy Call To Action (CTA Box) */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <div>
            <label className="unified-label">Philosophy Call to Action (CTA Box)</label>
            <p className="unified-field-desc" style={{ marginTop: '4px' }}>
              Kotak ajakan bergabung ("Ready to Make a Difference?") yang mengarahkan pengunjung ke daftar lowongan.
            </p>
          </div>
          {pendingFields.cta && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>

        <div style={{ background: '#f8fafc', padding: '18px', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '16px', marginTop: '14px' }}>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>
              CTA Title
            </label>
            <input
              type="text"
              className="unified-input"
              value={formData.cta.title || ''}
              disabled={pendingFields.cta}
              onChange={(e) => handleCtaChange('title', e.target.value)}
              placeholder="e.g. Ready to Make a Difference?"
            />
          </div>

          <div>
            <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>
              CTA Description
            </label>
            <textarea
              className="unified-textarea"
              rows={2}
              value={formData.cta.description || ''}
              disabled={pendingFields.cta}
              onChange={(e) => handleCtaChange('description', e.target.value)}
              placeholder="Join our dedicated team of educators and professionals..."
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: '14px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>
                Label Tombol (Button Text)
              </label>
              <input
                type="text"
                className="unified-input"
                value={formData.cta.button_label || ''}
                disabled={pendingFields.cta}
                onChange={(e) => handleCtaChange('button_label', e.target.value)}
                placeholder="e.g. Explore Open Positions"
              />
            </div>

            <div>
              <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>
                Tautan Tujuan Tombol (URL / Anchor) *
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  className="unified-input"
                  value={formData.cta.button_url || ''}
                  disabled={pendingFields.cta}
                  onChange={(e) => handleCtaChange('button_url', e.target.value)}
                  placeholder="e.g. #open-positions"
                  style={{ flex: 1 }}
                />
                {formData.cta.button_url && (
                  <a
                    href={formData.cta.button_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="unified-secondary-btn"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', textDecoration: 'none', padding: '0 14px', whiteSpace: 'nowrap' }}
                    title="Uji coba buka link"
                  >
                    Buka Link <ExternalLink size={13} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('cta', formData.cta, 'Philosophy Call to Action')}
            disabled={pendingFields.cta || submitting.cta}
          >
            <Save size={14} /> {submitting.cta ? 'Saving...' : (pendingFields.cta ? 'Pending' : 'Save Philosophy CTA')}
          </button>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          onClick={() => setLightboxImage(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              background: '#0f172a',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
            }}
          >
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'rgba(0, 0, 0, 0.6)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10
              }}
            >
              <X size={20} />
            </button>
            <img 
              src={lightboxImage} 
              alt="Preview" 
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '85vh',
                objectFit: 'contain'
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
